import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Send, Loader2, User, Users, Home } from "lucide-react";
import api from "../lib/api";
import { PageHeader } from "../components/kit";

const SUGGESTIONS = [
  "Show me all FSBO prospects in Idaho Falls that I have not contacted",
  "Who needs a follow-up today?",
  "Show me all seller prospects with properties over $500,000",
  "Which leads have not been contacted in 14 days?",
  "Show me all buyers looking under $400,000",
  "Find contacts associated with properties in Rigby",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const scrollRef = useRef();
  const nav = useNavigate();

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim() || loading) return;
    setInput(""); setMessages((m) => [...m, { role: "user", content: msg }]); setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg, session_id: session });
      setSession(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.answer, results: data.results, entity: data.entity }]);
    } catch { setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't process that." }]); }
    setLoading(false);
  };

  const openResult = (r, entity) => {
    if (entity === "contacts") nav(`/contacts/${r.id}`);
    else if (entity === "properties") nav(`/properties/${r.id}`);
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="AI Assistant" subtitle="Ask questions in plain English. I query your CRM and never invent data." testid="ai-page-header" />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col" style={{ height: "calc(100vh - 220px)" }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-5">
          {messages.length === 0 && (
            <div className="max-w-2xl">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-navy-800 to-navy-900 flex items-center justify-center mb-4"><Sparkles className="w-6 h-6 text-gold" /></div>
              <h3 className="font-display text-xl font-bold text-navy-800">How can I help you today?</h3>
              <p className="text-sm text-slate-500 mt-1 mb-5">Try one of these, or ask anything about your contacts, properties, and follow-ups.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} data-testid="ai-page-suggestion" className="text-left text-sm text-navy-800 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg px-4 py-3 transition-colors">{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-slate-200" : "bg-navy-800"}`}>
                {m.role === "user" ? <User className="w-4 h-4 text-slate-600" /> : <Sparkles className="w-4 h-4 text-gold" />}
              </div>
              <div className={`max-w-[75%] ${m.role === "user" ? "text-right" : ""}`}>
                <div className={`rounded-xl px-4 py-3 text-sm whitespace-pre-wrap inline-block text-left ${m.role === "user" ? "bg-navy-800 text-white" : "bg-slate-50 border border-slate-200 text-navy-800"}`}>{m.content}</div>
                {m.results?.length > 0 && (
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {m.results.slice(0, 8).map((r) => (
                      <button key={r.id} onClick={() => openResult(r, m.entity)} className="flex items-center gap-2 text-left bg-white hover:bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs transition-colors">
                        {m.entity === "properties" ? <Home className="w-3.5 h-3.5 text-gold-dark" /> : <Users className="w-3.5 h-3.5 text-gold-dark" />}
                        <span className="truncate">{m.entity === "properties" ? r.address : `${r.first_name || ""} ${r.last_name || ""}`.trim()}{r.city ? ` · ${r.city}` : ""}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && <div className="flex gap-3"><div className="w-8 h-8 rounded-lg bg-navy-800 flex items-center justify-center"><Sparkles className="w-4 h-4 text-gold" /></div><div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3"><Loader2 className="w-4 h-4 animate-spin text-gold-dark" /></div></div>}
        </div>
        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1 focus-within:border-gold transition-colors">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about your leads, properties, or follow-ups…" data-testid="ai-page-input" className="flex-1 bg-transparent text-sm outline-none py-2.5" />
            <button onClick={() => send()} disabled={loading} data-testid="ai-page-send" className="w-9 h-9 rounded-lg bg-navy-800 flex items-center justify-center text-white hover:bg-navy-700 disabled:opacity-40 transition-colors"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
